import {
  DefaultModalDialog,
  Select,
  Option,
  useToast,
} from '~/common/components/defaultUIComponents';
import { DragAndDropList } from '~/common/components/defaultUIComponents/DragAndDropList';
import DepartmentApi from '~/common/api/zeiterfassungServerApi/DepartmentApi';
import { JobApi } from '~/common/api/zeiterfassungServerApi/JobApi';
import { Job } from '~/common/entities/Project/ProjectTypes';
import { ListView } from '~/common/components/ListView';
import { useProjectContext } from './ProjectContext';
import { DropResult } from 'react-beautiful-dnd';
import { JobListItem } from './JobListItem';
import { Flex } from '@chakra-ui/react';
import React, { useState } from 'react';
import { useProject } from './utils';
import _ from 'lodash';

export const JobsList: React.FC = () => {
  const { selectValue } = useProjectContext();
  const { searchTerm } = useProjectContext();
  const { jobs } = useProject();
  const { mutate: editJob } = JobApi.hooks.useEditJobMutation();
  const [openDepartmentSelection, setOpenDepartmentSelection] = useState(false);
  const toast = useToast({});
  const [newDepartment, setNewDepartment] = useState(0);
  const [oldDepartment, setOldDepartment] = useState(0);
  const [jobToChange, setJobToChange] = useState<Job | undefined>(undefined);
  const [{ data: departments }] = DepartmentApi.hooks.useDepartmentsQuery();

  const onReorder = React.useCallback(
    (dropResult: DropResult) => {
      if (!_.isNil(dropResult.destination?.index)) {
        const job = _.get(jobs, dropResult.source.index);
        if (job) {
          editJob(
            {
              ...job,
              department: job?.department?.id,
              duration: job.duration as number,
              projects: _.map(job.projects, (project) => project.id) ?? [],
              step_number: (dropResult.destination?.index ?? 0) + 1,
            },
            {
              onSuccess: (data) => {
                const possibleNewDepartmentId = data?.possibleNewDepartmentId;
                if (possibleNewDepartmentId != job?.department?.id) {
                  setOldDepartment(job?.department?.id ?? 0);
                  setNewDepartment(job?.department?.id ?? 0);
                  setJobToChange(job);
                  setOpenDepartmentSelection(true);
                }
              },
            },
          );
        }
      }
    },
    [jobs, editJob],
  );

  const onDepartmentUpdate = async () => {
    try {
      if (newDepartment != oldDepartment) {
        await editJob({
          ...jobToChange!,
          department: newDepartment,
          duration: jobToChange!.duration as number,
          projects: _.map(jobToChange!.projects, (project) => project.id) ?? [],
        });
        setOpenDepartmentSelection(false);
      } else {
        setOpenDepartmentSelection(false);
      }
    } catch (e) {
      toast({ title: 'generic.error', status: 'error' });
    }
  };

  return (
    <Flex direction="column" justify="space-between" mb={3} mt={5}>
      <DefaultModalDialog
        dialogHeaderText="joblist.changeStepnumber.dialogHeaderText"
        submitText={
          oldDepartment === newDepartment
            ? 'joblist.changeStepnumber.submitText.keep'
            : 'joblist.changeStepnumber.submitText.change'
        }
        isOpen={openDepartmentSelection}
        onCancel={() => setOpenDepartmentSelection(false)}
        onSubmit={onDepartmentUpdate}
        showCancelButton={false}
        size="xl"
        initialValues={{ newProjectId: '' }}
      >
        <Select
          label="joblist.changeStepnumber.old.department.heading"
          value={oldDepartment}
          w="100%"
          ml={60}
          mb={2}
          isDisabled={true}
        >
          {departments &&
            _.orderBy(departments, ['id'], ['asc']).map((department) => (
              <Option key={department.id} value={department.id}>
                {department.name}
              </Option>
            ))}
        </Select>{' '}
        <Select
          label="joblist.changeStepnumber.select.department.heading"
          value={newDepartment}
          onChange={(e) => setNewDepartment(parseInt(e.currentTarget.value))}
          w="100%"
          ml={60}
          mb={2}
        >
          {departments &&
            _.orderBy(departments, ['id'], ['asc']).map((department) => (
              <Option key={department.id} value={department.id}>
                {department.name}
              </Option>
            ))}
        </Select>{' '}
      </DefaultModalDialog>
      <ListView
        searchFields={(job) => [job.name]}
        search={searchTerm}
        data={jobs.filter((job) => {
          return (
            selectValue === -1 ||
            parseInt(job.department?.id.toString() ?? '0') === selectValue
          );
        })}
        orderBy="step_number"
        render={({ data }) => (
          <DragAndDropList
            w="full"
            ids={data.map((job) => `${job.id}`)}
            listElements={data.map((job) => (
              <JobListItem key={job.id} job={job} />
            ))}
            onDropEnd={onReorder}
          />
        )}
      />
    </Flex>
  );
};
